import * as Portal from '@radix-ui/react-portal';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import { X } from 'lucide-react';
import * as React from 'react';
import { forwardRef, useEffect } from 'react';
import { cn } from 'utils/cn';
import Badge from '../Badge';
import Command from '../Command';
import useDebounce from '../useDebouncedValue';

export interface Option {
  value: string;
  label: string;
  disable?: boolean;
  /** fixed option that can't be removed. */
  fixed?: boolean;
  /** Group the options by providing key. */
  [key: string]: string | boolean | undefined;
}

// Add new interface for custom item renderer
export interface CustomItemProps {
  option: Option;
  selected: boolean;
  onSelect: (value: string) => void;
  disabled: boolean;
}

export interface CustomBadgeProps {
  option: Option;
  onUnselect: (option: Option) => void;
  disabled?: boolean;
  isContributor?: boolean;
  isvalidEmail?: boolean;
  creatable?: boolean;
}

interface GroupOption {
  [key: string]: Option[];
}

interface MultipleSelectorProps {
  value?: Option[];
  defaultOptions?: Option[];
  /** manually controlled options */
  options?: Option[];
  placeholder?: string;
  /** Loading component. */
  loadingIndicator?: React.ReactNode;
  /** Empty component. */
  emptyIndicator?: React.ReactNode;
  /** Debounce time for async search. Only work with `onSearch`. */
  delay?: number;
  /**
   * Only work with `onSearch` prop. Trigger search when `onFocus`.
   * For example, when user click on the input, it will trigger the search to get initial options.
   **/
  triggerSearchOnFocus?: boolean;
  /** async search */
  onSearch?: (value: string) => Promise<Option[]>;
  /**
   * sync search. This search will not showing loadingIndicator.
   * The rest props are the same as async search.
   * i.e.: creatable, groupBy, delay.
   **/
  onSearchSync?: (value: string) => Option[];
  onChange?: (options: Option[]) => void;
  /** Limit the maximum number of selected options. */
  maxSelected?: number;
  /** When the number of selected options exceeds the limit, the onMaxSelected will be called. */
  onMaxSelected?: (maxLimit: number) => void;
  /** Hide the placeholder when there are options selected. */
  hidePlaceholderWhenSelected?: boolean;
  disabled?: boolean;
  /** Group the options base on provided key. */
  groupBy?: string;
  className?: string;
  badgeClassName?: string;
  /**
   * First item selected is a default behavior by cmdk. That is why the default is true.
   * This is a workaround solution by add a dummy item.
   *
   * @reference: https://github.com/pacocoursey/cmdk/issues/171
   */
  selectFirstItem?: boolean;
  /** Allow user to create option when there is no option matched. */
  creatable?: boolean;
  /** creatable prefix */
  creatablePrefix?: string;
  /** Allow whitespace in option values. When true, space/tab keys won't trigger submission but will be included in the value. */
  allowWhitespace?: boolean;
  /** Props of `Command` */
  commandProps?: React.ComponentPropsWithoutRef<typeof Command>;
  /** Props of `CommandInput` */
  inputProps?: Omit<
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
    'value' | 'placeholder' | 'disabled'
  >;
  /** React node to be rendered on the left side of the input. */
  leftSection?: React.ReactNode;
  /** React node to be rendered on the right side of the input. */
  rightSection?: React.ReactNode;
  /** hide the clear all button. */
  hideClearAllButton?: boolean;
  showDropdown?: boolean;
  /** Custom component to render each select item */
  customItemComponent?: React.ComponentType<CustomItemProps>;
  /** Custom component to render selected badges */
  customBadgeComponent?: React.ComponentType<CustomBadgeProps>;
  autoFocus?: boolean;
  /** Render the dropdown list in a portal */
  portal?: boolean;
}

export interface MultipleSelectorRef {
  selectedValue: Option[];
  input: HTMLInputElement;
}

function transToGroupOption(options: Option[], groupBy?: string) {
  if (options.length === 0) {
    return {};
  }
  if (!groupBy) {
    return {
      '': options,
    };
  }

  const groupOption: GroupOption = {};
  options.forEach((option) => {
    const key = (option[groupBy] as string) || '';
    if (!groupOption[key]) {
      groupOption[key] = [];
    }
    groupOption[key].push(option);
  });
  return groupOption;
}

function removePickedOption(groupOption: GroupOption, picked: Option[]) {
  const cloneOption = JSON.parse(JSON.stringify(groupOption)) as GroupOption;

  for (const [key, value] of Object.entries(cloneOption)) {
    cloneOption[key] = value.filter(
      (val) => !picked.find((p) => p.value === val.value)
    );
  }
  return cloneOption;
}

function isOptionsExist(groupOption: GroupOption, targetOption: Option[]) {
  for (const [, value] of Object.entries(groupOption)) {
    if (
      value.some((option) => targetOption.find((p) => p.value === option.value))
    ) {
      return true;
    }
  }
  return false;
}

const CommandEmpty = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CommandPrimitive.Empty>
>(({ className, ...props }, forwardedRef) => {
  const render = useCommandState((state) => state.filtered.count === 0);

  if (!render) return null;

  return (
    <div
      ref={forwardedRef}
      className={cn('py-6 text-center text-sm', className)}
      cmdk-empty=""
      role="presentation"
      {...props}
    />
  );
});

CommandEmpty.displayName = 'CommandEmpty';

const MultiSelect = React.forwardRef<MultipleSelectorRef, MultipleSelectorProps>(
  (
    {
      value,
      onChange,
      placeholder,
      defaultOptions: arrayDefaultOptions = [],
      options: arrayOptions,
      delay,
      onSearch,
      onSearchSync,
      loadingIndicator,
      emptyIndicator,
      maxSelected = Number.MAX_SAFE_INTEGER,
      onMaxSelected,
      hidePlaceholderWhenSelected,
      disabled,
      groupBy,
      className,
      badgeClassName,
      selectFirstItem = true,
      creatable = false,
      creatablePrefix = 'Create',
      allowWhitespace = false,
      triggerSearchOnFocus = false,
      commandProps,
      inputProps,
      hideClearAllButton = false,
      leftSection,
      rightSection,
      showDropdown = true,
      customItemComponent: CustomItem,
      customBadgeComponent: CustomBadge,
      autoFocus = false,
      portal = false,
    }: MultipleSelectorProps,
    ref: React.Ref<MultipleSelectorRef>
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const dropdownListRef = React.useRef<HTMLDivElement>(null);
    const [open, setOpen] = React.useState(false);
    const [onScrollbar, setOnScrollbar] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [selected, setSelected] = React.useState<Option[]>(value || []);
    const [options, setOptions] = React.useState<GroupOption>(
      transToGroupOption(arrayDefaultOptions, groupBy)
    );
    const [inputValue, setInputValue] = React.useState('');
    const debouncedSearchTerm = useDebounce(inputValue, delay || 500);
    const [dropdownPosition, setDropdownPosition] = React.useState({
      top: 0,
      left: 0,
      width: 0,
    });

    React.useImperativeHandle(
      ref,
      () => ({
        selectedValue: [...selected],
        input: inputRef.current as HTMLInputElement,
        focus: () => inputRef.current?.focus(),
      }),
      [selected]
    );

    // Enhanced function to add input value as new option
    const addInputValueAsOption = React.useCallback(() => {
      const processedValue = allowWhitespace ? inputValue : inputValue.trim();

      // Only add if we have a non-empty value (or allow empty when whitespace is allowed),
      // haven't reached max selection, and the value doesn't already exist
      if (
        (processedValue || (allowWhitespace && inputValue)) &&
        selected.length < maxSelected &&
        !selected.find((s) => s.value === processedValue) &&
        !isOptionsExist(options, [{ value: processedValue, label: processedValue }])
      ) {
        setInputValue('');
        const newOptions = [...selected, { value: processedValue, label: processedValue }];
        setSelected(newOptions);
        onChange?.(newOptions);
        return true;
      }

      if (selected.length >= maxSelected) {
        onMaxSelected?.(selected.length);
      }

      return false;
    }, [inputValue, selected, maxSelected, onChange, onMaxSelected, options, allowWhitespace]);

    // Update dropdown position for portal mode
    useEffect(() => {
      if (open && portal && dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    }, [open, portal]);

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        (dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current &&
          !inputRef.current.contains(event.target as Node)) &&
        (!dropdownListRef.current ||
          !dropdownListRef.current.contains(event.target as Node))
      ) {
        // Enhanced: Try to add input value when clicking outside
        if (creatable && inputValue && (allowWhitespace || inputValue.trim())) {
          addInputValueAsOption();
        }
        setOpen(false);
      }
    };

    const handleUnselect = React.useCallback(
      (option: Option) => {
        const newOptions = selected.filter((s) => s.value !== option.value);
        setSelected(newOptions);
        onChange?.(newOptions);
      },
      [onChange, selected]
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            if (input.value === '' && selected.length > 0) {
              const lastSelectOption = selected[selected.length - 1];
              if (!lastSelectOption.fixed) {
                handleUnselect(selected[selected.length - 1]);
              }
            }
          }
          if (e.key === 'Escape') {
            input.blur();
          }
        }
      },
      [handleUnselect, selected]
    );

    useEffect(() => {
      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchend', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchend', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchend', handleClickOutside);
      };
    }, [open, inputValue, creatable, addInputValueAsOption, allowWhitespace]);

    useEffect(() => {
      if (value) {
        setSelected(value);
      }
    }, [value]);

    useEffect(() => {
      if (!arrayOptions || onSearch) {
        return;
      }
      const newOption = transToGroupOption(arrayOptions || [], groupBy);
      if (JSON.stringify(newOption) !== JSON.stringify(options)) {
        setOptions(newOption);
      }
    }, [arrayDefaultOptions, arrayOptions, groupBy, onSearch, options]);

    useEffect(() => {
      const doSearchSync = () => {
        const res = onSearchSync?.(debouncedSearchTerm);
        setOptions(transToGroupOption(res || [], groupBy));
      };

      const exec = async () => {
        if (!onSearchSync || !open) return;

        if (triggerSearchOnFocus) {
          doSearchSync();
        }

        if (debouncedSearchTerm) {
          doSearchSync();
        }
      };

      void exec();
    }, [debouncedSearchTerm, groupBy, onSearchSync, open, triggerSearchOnFocus]);

    useEffect(() => {
      const doSearch = async () => {
        setIsLoading(true);
        const res = await onSearch?.(debouncedSearchTerm);
        setOptions(transToGroupOption(res || [], groupBy));
        setIsLoading(false);
      };

      const exec = async () => {
        if (!onSearch || !open) return;

        if (triggerSearchOnFocus) {
          await doSearch();
        }

        if (debouncedSearchTerm) {
          await doSearch();
        }
      };

      void exec();
    }, [debouncedSearchTerm, groupBy, onSearch, open, triggerSearchOnFocus]);

    const renderBadge = (option: Option) => {
      if (CustomBadge) {
        return (
          <CustomBadge
            key={option.value}
            option={option}
            onUnselect={handleUnselect}
            disabled={disabled}
            creatable={creatable}
          />
        );
      }

      return (
        <Badge
          key={option.value}
          variant="tertiary"
          className={cn(
            'data-[disabled]:bg-muted-foreground data-[disabled]:hover:bg-muted-foreground data-[disabled]:text-muted px-2 py-1 gap-1',
            'data-[fixed]:bg-muted-foreground data-[fixed]:hover:bg-muted-foreground data-[fixed]:text-muted',
            badgeClassName
          )}
          data-fixed={option?.fixed}
          data-disabled={disabled || undefined}
        >
          {option.label}
          <button
            className={cn(
              'ring-offset-background focus:ring-ring rounded-full outline-none focus:ring-2 focus:ring-offset-2',
              (disabled || option.fixed) && 'hidden'
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleUnselect(option);
              }
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() => handleUnselect(option)}
          >
            <X className="text-muted-foreground hover:text-foreground h-3 w-3" />
          </button>
        </Badge>
      );
    };

    const renderSelectItem = (option: Option) => {
      const handleSelect = () => {
        if (selected.length >= maxSelected) {
          onMaxSelected?.(selected.length);
          return;
        }
        setInputValue('');
        const newOptions = [...selected, option];
        setSelected(newOptions);
        onChange?.(newOptions);
      };

      if (CustomItem) {
        return (
          <Command.Item
            key={option.value}
            keywords={Object.values(option).filter((value): value is string =>
              typeof value === 'string'
            )}
            value={option.value}
            disabled={option.disable}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onSelect={handleSelect}
            className={cn(
              'cursor-pointer',
              option.disable && 'text-muted-foreground cursor-default'
            )}
          >
            <CustomItem
              option={option}
              selected={selected.some((item) => item.value === option.value)}
              onSelect={handleSelect}
              disabled={!!option.disable}
            />
          </Command.Item>
        );
      }

      return (
        <Command.Item
          key={option.value}
          keywords={[option.value, option.label]}
          value={option.value}
          disabled={option.disable || selected.length >= maxSelected}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onSelect={handleSelect}
          className={cn(
            'cursor-pointer',
            option.disable && 'text-muted-foreground cursor-default'
          )}
        >
          <div id={option.value}>
            <span className="hidden">{option.value}</span>
            {option.label}
          </div>
        </Command.Item>
      );
    };

    const CreatableItem = () => {
      if (!creatable) return undefined;
      if (
        isOptionsExist(options, [{ value: inputValue, label: inputValue }]) ||
        selected.find((s) => s.value === inputValue)
      ) {
        return undefined;
      }

      const Item = (
        <Command.Item
          value={inputValue}
          className="cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onSelect={(value: string) => {
            if (selected.length >= maxSelected) {
              onMaxSelected?.(selected.length);
              return;
            }
            setInputValue('');
            const newOptions = [...selected, { value, label: value }];
            setSelected(newOptions);
            onChange?.(newOptions);
          }}
        >
          {`${creatablePrefix} "${inputValue}"`}
        </Command.Item>
      );

      if (!onSearch && inputValue.length > 0) {
        return Item;
      }

      if (onSearch && debouncedSearchTerm.length > 0 && !isLoading) {
        return Item;
      }

      return undefined;
    };

    const EmptyItem = React.useCallback(() => {
      if (!emptyIndicator) return undefined;

      if (onSearch && !creatable && Object.keys(options).length === 0) {
        return (
          <Command.Item value="-" disabled>
            {emptyIndicator}
          </Command.Item>
        );
      }

      return <CommandEmpty>{emptyIndicator}</CommandEmpty>;
    }, [creatable, emptyIndicator, onSearch, options]);

    const selectables = React.useMemo<GroupOption>(
      () => removePickedOption(options, selected),
      [options, selected]
    );

    const commandFilter = React.useCallback(() => {
      if (commandProps?.filter) {
        return commandProps.filter;
      }

      if (creatable) {
        return (value: string, search: string) => {
          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : -1;
        };
      }
      return (value: string, search: string, keywords?: string[]) => {
        const extendValue = value + ' ' + (keywords || []).join(' ')
        if (extendValue.toLowerCase().includes(search.toLowerCase())) return 1
        return 0
      };
    }, [creatable, commandProps?.filter]);

    const renderDropdownContent = () => (
      <Command.List
        ref={dropdownListRef}
        className={cn(
          "min-h-10 max-h-[300px] overflow-y-auto text-popover-foreground bg-popover animate-in w-full rounded-md border shadow-md outline-none",
          portal ? "" : "absolute top-1 z-10"
        )}
        onMouseLeave={() => {
          setOnScrollbar(false);
        }}
        onMouseEnter={() => {
          setOnScrollbar(true);
        }}
        onMouseUp={() => {
          inputRef.current?.focus();
        }}
        // Add wheel event handler to ensure proper scrolling
        onWheel={(e) => {
          // Prevent the parent from scrolling when dropdown is being scrolled
          e.stopPropagation();
        }}
      >
        {isLoading ? (
          <>{loadingIndicator}</>
        ) : (
          <>
            {EmptyItem()}
            {CreatableItem()}
            {!selectFirstItem && (
              <Command.Item value="-" className="hidden" />
            )}
            {Object.entries(selectables).map(([key, dropdowns]) => (
              <Command.Group
                key={key}
                heading={key}
                // Remove overflow-auto from Command.Group to prevent nested scrollable areas
                className="h-full"
              >
                {dropdowns.map((option) => renderSelectItem(option))}
              </Command.Group>
            ))}
          </>
        )}
      </Command.List>
    );

    return (
      <Command
        ref={dropdownRef}
        {...commandProps}
        onKeyDown={(e) => {
          handleKeyDown(e);
          commandProps?.onKeyDown?.(e);
        }}
        className={cn(
          'h-auto overflow-visible bg-transparent',
          commandProps?.className
        )}
        shouldFilter={
          commandProps?.shouldFilter !== undefined
            ? commandProps.shouldFilter
            : !onSearch
        }
        filter={commandFilter()}
      >
        <div
          className={cn(
            'ring-offset-background focus-within:ring-ring border-input flex min-h-10 items-center rounded-md border px-1.5 text-sm focus-within:ring-2 focus-within:ring-offset-2',
            {
              'py-1': selected.length !== 0,
              'cursor-text': !disabled && selected.length !== 0,
            },
            className
          )}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
          }}
        >
          {leftSection}
          <div className="relative flex grow flex-wrap gap-1">
            {selected.map((option) => renderBadge(option))}
            <CommandPrimitive.Input
              {...inputProps}
              ref={inputRef}
              value={inputValue}
              disabled={disabled}
              onValueChange={(value) => {
                // Enhanced: Handle whitespace submission only if whitespace is not allowed
                if (creatable && !allowWhitespace && (value.endsWith(' ') || value.endsWith('\t'))) {
                  // Set value without the whitespace first
                  const trimmedValue = value.trim();
                  setInputValue(trimmedValue);

                  // Then try to add it as an option
                  if (trimmedValue) {
                    // Use setTimeout to ensure the input value is updated first
                    setTimeout(() => {
                      addInputValueAsOption();
                    }, 0);
                  }
                } else {
                  setInputValue(value);
                }
                inputProps?.onValueChange?.(value);
              }}
              onBlur={(event) => {
                // Enhanced: Try to add input value on blur (when clicking outside)
                if (!onScrollbar) {
                  if (creatable && inputValue && (allowWhitespace || inputValue.trim())) {
                    addInputValueAsOption();
                  }
                  setOpen(false);
                }
                inputProps?.onBlur?.(event);
              }}
              onFocus={(event) => {
                setOpen(true);
                triggerSearchOnFocus && onSearch?.(debouncedSearchTerm);
                inputProps?.onFocus?.(event);
              }}
              onKeyDown={(e) => {
                // Enhanced: Handle multiple submission methods
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (creatable && inputValue && (allowWhitespace || inputValue.trim())) {
                    const wasAdded = addInputValueAsOption();
                    if (wasAdded) return;
                  } else if (!inputValue.trim()) {
                    // Allow default behavior for empty Enter (might select from dropdown)
                    return;
                  }
                } else if (!allowWhitespace && (e.key === ' ' || e.key === 'Tab')) {
                  // Only trigger submission on space/tab if whitespace is not allowed
                  if (creatable && inputValue.trim()) {
                    e.preventDefault(); // Prevent default behavior for space and tab
                    addInputValueAsOption();
                  }
                }
              }}
              placeholder={
                hidePlaceholderWhenSelected && selected.length !== 0
                  ? ''
                  : placeholder
              }
              className={cn(
                'placeholder:text-muted-foreground flex-1 bg-transparent outline-none',
                {
                  'w-full': hidePlaceholderWhenSelected,
                  'px-1.5 py-0': selected.length === 0,
                  'ml-1': selected.length !== 0,
                },
                inputProps?.className
              )}
              autoFocus={autoFocus}
            />
            <button
              type="button"
              onClick={() => {
                setSelected(selected.filter((s) => s.fixed));
                onChange?.(selected.filter((s) => s.fixed));
              }}
              className={cn(
                'absolute right-0 h-6 w-6 p-0',
                (hideClearAllButton ||
                  disabled ||
                  selected.length < 1 ||
                  selected.filter((s) => s.fixed).length === selected.length) &&
                'hidden'
              )}
            >
              <X />
            </button>
          </div>
          {rightSection}
        </div>

        <div className="relative">
          {open && showDropdown && (
            <>
              {portal ? (
                <Portal.Root container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper') || document.body}>
                  <div
                    style={{
                      position: 'absolute',
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      width: `${dropdownPosition.width}px`,
                      zIndex: 9999,
                      pointerEvents: 'auto',
                    }}
                  >
                    {renderDropdownContent()}
                  </div>
                </Portal.Root>
              ) : (
                renderDropdownContent()
              )}
            </>
          )}
        </div>
      </Command>
    );
  }
);

MultiSelect.displayName = 'MultipleSelector';
export default MultiSelect;